import { classNames, niceNumber } from "@/lib/util";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import Tooltip from "@/modules/shared/ui/Tooltip";

import type { IPlanScore } from "@/lib/backend/manufacturing2.generated.types";
import type { IPlanScoreElements } from "@/lib/backend/manufacturing2.generated.types";

type Props = {
    plan_score?: IPlanScore;
    base_plan_score?: IPlanScore;
};

type ScoreElementKey = keyof IPlanScoreElements;

const SCORE_ELEMENTS = {
    neg_stock: "neg_stock",
    neg_ex_stock: "neg_ex_stock",
    pos_stock: "pos_stock",
    pos_ex_stock: "pos_ex_stock",
    need: "need",
    setup: "setup",
    work_duration: "work_duration",
    diff_material_person: "diff_material_person",
    fragment_person: "fragment_person",
    fragment_line: "fragment_line",
    setup_code: "setup_code",
} as const;

const getScore = (plan_score?: IPlanScore): number => {
    if (plan_score === undefined) {
        return NaN;
    }

    return plan_score.score;
};

const getScoreElementValue = (key: ScoreElementKey, plan_score?: IPlanScore): number => {
    if (plan_score === undefined) {
        return NaN;
    }

    return plan_score.score_elements[key];
};

const renderNumber = (value: number): string => {
    if (Number.isNaN(value)) {
        return "-";
    }

    if (!Number.isFinite(value)) {
        return "∞";
    }

    return niceNumber(value, 2);
};

const renderPercents = (value: number): string => {
    if (Number.isNaN(value)) {
        return "-";
    }

    if (!Number.isFinite(value)) {
        return "∞";
    }

    return `${niceNumber(value * 100, 2)}%`;
};

const ScoreCell = (props: { score: number; base_score: number }) => {
    const { score, base_score } = props;
    const diff = score - base_score;
    return (
        <td
            className={classNames({
                stock_available_lite: !Number.isNaN(diff) && diff < 0,
                stock_empty: Number.isNaN(diff) || diff === 0,
                stock_missing_lite: !Number.isNaN(diff) && diff > 0,
            })}
        >
            <span>
                <strong>{renderNumber(score)}</strong> / {renderNumber(base_score)} (
                {renderPercents(score / base_score)})
            </span>
        </td>
    );
};

const PlanScoresTable = (props: Props) => {
    const { plan_score, base_plan_score } = props;
    const score = getScore(plan_score);
    const base_score = getScore(base_plan_score);
    const score_explanation = (plan_score && plan_score.explain) || "";

    return (
        <table className="table">
            <thead>
                <tr>
                    <th>{translate("common.property", "Property")}</th>
                    <th>{translate("common.value", "Value")}</th>
                </tr>
            </thead>
            <tbody>
                {(Object.keys(SCORE_ELEMENTS) as ScoreElementKey[]).map(key => {
                    const score_element_value = getScoreElementValue(key, plan_score);
                    const base_score_element_value = getScoreElementValue(key, base_plan_score);
                    return (
                        <tr key={key}>
                            <td>
                                {key}{" "}
                                <Tooltip content={translate(`Manufacturing.Planning.Score.description.${key}`)}>
                                    <sup className="fas fa-info-circle" />
                                </Tooltip>
                            </td>
                            <ScoreCell score={score_element_value} base_score={base_score_element_value} />
                        </tr>
                    );
                })}
                <tr>
                    <td>
                        <strong>
                            {translate("common.score", "Score")}{" "}
                            {score_explanation !== "" && (
                                <Tooltip content={((plan_score && plan_score.explain) || "").replace(/\*/g, "×")}>
                                    <sup className="fas fa-info-circle" />
                                </Tooltip>
                            )}
                        </strong>
                    </td>
                    <ScoreCell score={score} base_score={base_score} />
                </tr>
            </tbody>
        </table>
    );
};

export default PlanScoresTable;
